import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph } from './_finHelpers';
import { CNetActions } from '../other/_cnet';

// Retirement Calculator — 4 sub-calcs matching Calculator.net
export default function RetirementCalc() {
  const [tab, setTab] = useState('need');
  const TABS = [
    ['need', 'How much do I need to retire?'],
    ['contrib', 'How can I save for retirement?'],
    ['withdraw', 'How much can I withdraw after retirement?'],
    ['life', 'How long will my money last?'],
  ];
  return (
    <CalcShell showActions={false} look="cnet" title="Retirement Calculator" subtitle="Answer the four biggest retirement-planning questions in one place.">
      <div className="flex flex-wrap gap-0 mb-0">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`ret-tab-${v}`}
            onClick={() => setTab(v)}
            className={`cnet-tab ${tab === v ? 'active' : ''}`}>{l}</button>
        ))}
      </div>
      <div className="cnet-tab-body">
        {tab === 'need' && <NeedForRetirement />}
        {tab === 'contrib' && <ContributionNeeded />}
        {tab === 'withdraw' && <WithdrawAmount />}
        {tab === 'life' && <LifeExpectancy />}
      </div>
    </CalcShell>
  );
}

/* -------- 1. How much do I need to retire? -------- */
function NeedForRetirement() {
  const D = { age: 35, retireAge: 67, lifeExp: 85, income: 70000, incInc: 3, needPct: 75, returnRate: 6, inflation: 3, otherIncMo: 0, curSavings: 30000, savePct: 10 };
  const [age, setAge] = useState(D.age);
  const [retireAge, setRetireAge] = useState(D.retireAge);
  const [lifeExp, setLifeExp] = useState(D.lifeExp);
  const [income, setIncome] = useState(D.income);
  const [incInc, setIncInc] = useState(D.incInc);
  const [needPct, setNeedPct] = useState(D.needPct);
  const [returnRate, setReturnRate] = useState(D.returnRate);
  const [inflation, setInflation] = useState(D.inflation);
  const [otherIncMo, setOtherIncMo] = useState(D.otherIncMo);
  const [curSavings, setCurSavings] = useState(D.curSavings);
  const [savePct, setSavePct] = useState(D.savePct);
  const [show, setShow] = useState(true);
  const c = useMemo(() => {
    const yearsToRet = retireAge - age;
    const yearsInRet = lifeExp - retireAge;
    const r = returnRate / 100, infl = inflation / 100;
    let sal = income, bal = curSavings;
    const rows = [];
    for (let y = 1; y <= yearsToRet; y++) {
      const contrib = sal * savePct / 100;
      bal = bal * (1 + r) + contrib;
      sal = sal * (1 + incInc / 100);
      rows.push({ year: age + y, salary: sal, balance: bal, contrib });
    }
    const salAtRet = sal;
    const annualNeed = salAtRet * needPct / 100 - otherIncMo * 12;
    const realR = (1 + r) / (1 + infl) - 1;
    const factor = realR === 0 ? yearsInRet : (1 - Math.pow(1 + realR, -yearsInRet)) / realR;
    const needAtRet = annualNeed * factor;
    return { yearsToRet, yearsInRet, balAtRet: bal, salAtRet, annualNeed, needAtRet, gap: needAtRet - bal, rows };
  }, [age, retireAge, lifeExp, income, incInc, needPct, returnRate, inflation, otherIncMo, curSavings, savePct]);
  const clear = () => { setAge(D.age); setRetireAge(D.retireAge); setLifeExp(D.lifeExp); setIncome(D.income); setIncInc(D.incInc); setNeedPct(D.needPct); setReturnRate(D.returnRate); setInflation(D.inflation); setOtherIncMo(D.otherIncMo); setCurSavings(D.curSavings); setSavePct(D.savePct); setShow(false); };
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your current age</label><input type="number" value={age} onChange={e => setAge(+e.target.value || 0)} /></div>
          <div className="field"><label>Your planned retirement age</label><input type="number" value={retireAge} onChange={e => setRetireAge(+e.target.value || 0)} /></div>
          <div className="field"><label>Your life expectancy</label><input type="number" value={lifeExp} onChange={e => setLifeExp(+e.target.value || 0)} /></div>
          <div className="field"><label>Your current pre-tax income</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={income} onChange={e => setIncome(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">/year</span></div></div>
          <div className="field"><label>Current annual savings amount</label><div className="flex gap-2"><input type="number" step="0.1" value={savePct} onChange={e => setSavePct(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">% of income</span></div></div>
          <div className="field"><label>Current retirement savings</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={curSavings} onChange={e => setCurSavings(+e.target.value || 0)} className="flex-1" /></div></div>
          <div className="field"><label>Expected income increase</label><div className="flex gap-2"><input type="number" step="0.1" value={incInc} onChange={e => setIncInc(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">% / year</span></div></div>
          <div className="field"><label>Income needed after retirement</label><div className="flex gap-2"><input type="number" step="1" value={needPct} onChange={e => setNeedPct(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">% of pre-retirement</span></div></div>
          <div className="field"><label>Investment return rate</label><div className="flex gap-2"><input type="number" step="0.1" value={returnRate} onChange={e => setReturnRate(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">% / year</span></div></div>
          <div className="field"><label>Inflation rate</label><div className="flex gap-2"><input type="number" step="0.1" value={inflation} onChange={e => setInflation(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">% / year</span></div></div>
          <div className="field"><label>Other retirement income</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={otherIncMo} onChange={e => setOtherIncMo(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">/month</span></div></div>
          <CNetActions onCalc={() => setShow(true)} onClear={clear} testId="ret-need" />
        </div>
        <div>
          {show ? (<>
            <div className="result-panel mb-4"><div className="result-title">Amount needed at retirement</div><div className="result-value" data-testid="ret-need-value">{fmt(c.needAtRet)}</div></div>
            <table className="data-table"><tbody>
              <tr><td>Years until retirement</td><td className="text-right">{c.yearsToRet}</td></tr>
              <tr><td>Years in retirement</td><td className="text-right">{c.yearsInRet}</td></tr>
              <tr><td>Pre-tax income at retirement (in {c.yearsToRet} yrs)</td><td className="text-right">{fmt(c.salAtRet)}</td></tr>
              <tr><td>Annual expenses in retirement (year 1)</td><td className="text-right">{fmt(c.annualNeed)}</td></tr>
              <tr><td>Projected savings at retirement</td><td className="text-right font-semibold">{fmt(c.balAtRet)}</td></tr>
              <tr className="border-t-2 font-semibold"><td>{c.gap >= 0 ? 'Shortfall' : 'Surplus'}</td><td className={`text-right font-bold ${c.gap >= 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(Math.abs(c.gap))}</td></tr>
            </tbody></table>
            <div className="mt-4"><PieChart parts={[
              { label: 'Projected savings', value: c.balAtRet, color: '#2563eb' },
              { label: c.gap >= 0 ? 'Shortfall' : 'Surplus', value: Math.abs(c.gap), color: c.gap >= 0 ? '#dc2626' : '#10b981' },
            ]} size={170} /></div>
          </>) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see your retirement need.</div>)}
        </div>
      </div>
      {show && <div className="border border-slate-200 rounded-lg p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Growth to Retirement</h3>
        <BalanceGraph yearly={c.rows.map(r => ({ year: r.year, balance: r.balance, interest: 0, principal: r.contrib, payment: r.contrib }))} startBalance={curSavings} showPayment={false} />
      </div>}
      {show && <StepsPanel steps={[
        `At retirement (age ${retireAge}) you'll need <b>${fmt(c.annualNeed)}</b> per year × factor ${((c.needAtRet / c.annualNeed) || 0).toFixed(2)} = <b>${fmt(c.needAtRet)}</b>.`,
        `Projected savings by retirement: <b>${fmt(c.balAtRet)}</b>. ${c.gap >= 0 ? 'Shortfall' : 'Surplus'}: <b>${fmt(Math.abs(c.gap))}</b>.`,
      ]} />}
    </>
  );
}

/* -------- 2. How can I save for retirement? -------- */
function ContributionNeeded() {
  const D = { age: 35, retireAge: 67, target: 600000, curSavings: 30000, returnRate: 6 };
  const [age, setAge] = useState(D.age);
  const [retireAge, setRetireAge] = useState(D.retireAge);
  const [target, setTarget] = useState(D.target);
  const [curSavings, setCurSavings] = useState(D.curSavings);
  const [returnRate, setReturnRate] = useState(D.returnRate);
  const [show, setShow] = useState(true);
  const c = useMemo(() => {
    const yearsToRet = Math.max(0, retireAge - age);
    const n = yearsToRet * 12;
    const r = returnRate / 100 / 12;
    const fvFactor = r === 0 ? n : (Math.pow(1 + r, n) - 1) / r;
    const fvOfCurrent = curSavings * Math.pow(1 + r, n);
    const gap = Math.max(0, target - fvOfCurrent);
    const monthly = fvFactor === 0 ? 0 : gap / fvFactor;
    // Alternative: contribute annually
    const yFactor = returnRate === 0 ? yearsToRet : (Math.pow(1 + returnRate/100, yearsToRet) - 1) / (returnRate/100);
    const annual = yFactor === 0 ? 0 : gap / yFactor;
    return { monthly, annual, gap, fvOfCurrent, yearsToRet, n };
  }, [age, retireAge, curSavings, target, returnRate]);
  const clear = () => { setAge(D.age); setRetireAge(D.retireAge); setCurSavings(D.curSavings); setTarget(D.target); setReturnRate(D.returnRate); setShow(false); };
  return (
    <>
      <h3 className="text-xl font-bold text-slate-900 mb-1">How can you save for retirement?</h3>
      <p className="text-sm text-slate-600 mb-4">This calculation presents potential savings plans based on desired savings at retirement.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your age now</label><input type="number" value={age} onChange={e => setAge(+e.target.value || 0)} data-testid="ret-save-age"/></div>
          <div className="field"><label>Your planned retirement age</label><input type="number" value={retireAge} onChange={e => setRetireAge(+e.target.value || 0)} data-testid="ret-save-retage"/></div>
          <div className="field"><label>Amount needed at the retirement age</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={target} onChange={e => setTarget(+e.target.value || 0)} className="flex-1" data-testid="ret-save-target"/></div></div>
          <div className="field"><label>Your retirement savings now</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={curSavings} onChange={e => setCurSavings(+e.target.value || 0)} className="flex-1"/></div></div>
          <div className="field"><label>Average investment return</label><div className="flex gap-2"><input type="number" step="0.1" value={returnRate} onChange={e => setReturnRate(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <CNetActions onCalc={() => setShow(true)} onClear={clear} testId="ret-contrib" />
        </div>
        <div>
          {show ? (<>
            <div className="result-panel mb-4"><div className="result-title">You need to save</div><div className="result-value" data-testid="ret-contrib-value">{fmt(c.monthly)}<span className="text-base font-normal"> / month</span></div></div>
            <table className="data-table"><tbody>
              <tr><td>Or, equivalently, an annual contribution of</td><td className="text-right font-semibold">{fmt(c.annual)}</td></tr>
              <tr><td>Future value of current savings</td><td className="text-right">{fmt(c.fvOfCurrent)}</td></tr>
              <tr><td>Gap to close via contributions</td><td className="text-right text-orange-600">{fmt(c.gap)}</td></tr>
              <tr><td>Years to retirement</td><td className="text-right">{c.yearsToRet}</td></tr>
            </tbody></table>
            <p className="text-xs text-slate-500 mt-2">Assumes returns are compounded monthly and contributions are made at the end of each month.</p>
          </>) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see your required monthly contribution.</div>)}
        </div>
      </div>
    </>
  );
}

/* -------- 3. How much can I withdraw after retirement? -------- */
function WithdrawAmount() {
  const D = { age: 35, retireAge: 67, lifeExp: 85, curSavings: 30000, annualContrib: 0, monthlyContrib: 500, returnRate: 6, inflation: 3 };
  const [age, setAge] = useState(D.age);
  const [retireAge, setRetireAge] = useState(D.retireAge);
  const [lifeExp, setLifeExp] = useState(D.lifeExp);
  const [curSavings, setCurSavings] = useState(D.curSavings);
  const [annualContrib, setAnnualContrib] = useState(D.annualContrib);
  const [monthlyContrib, setMonthlyContrib] = useState(D.monthlyContrib);
  const [returnRate, setReturnRate] = useState(D.returnRate);
  const [inflation, setInflation] = useState(D.inflation);
  const [show, setShow] = useState(true);
  const c = useMemo(() => {
    const yearsToRet = Math.max(0, retireAge - age);
    const yearsInRet = Math.max(0, lifeExp - retireAge);
    const r = returnRate / 100;
    const rM = Math.pow(1 + r, 1/12) - 1;
    // Accumulate savings until retirement
    let bal = curSavings;
    const growthRows = [];
    for (let y = 1; y <= yearsToRet; y++) {
      for (let m = 0; m < 12; m++) bal = bal * (1 + rM) + monthlyContrib;
      bal += annualContrib;
      growthRows.push({ year: age + y, balance: bal });
    }
    const nestAtRet = bal;
    // Compute monthly withdrawal (real terms, inflation-adjusted) for yearsInRet
    const realR = (1 + r) / (1 + inflation/100) - 1;
    const realRm = Math.pow(1 + realR, 1/12) - 1;
    const n = yearsInRet * 12;
    const monthly = realRm === 0 ? nestAtRet / n : (nestAtRet * realRm) / (1 - Math.pow(1 + realRm, -n));
    // Also show nominal (non-adjusted) equivalent
    const monthlyNominal = rM === 0 ? nestAtRet / n : (nestAtRet * rM) / (1 - Math.pow(1 + rM, -n));
    return { nestAtRet, monthly, monthlyNominal, yearsToRet, yearsInRet, growthRows };
  }, [age, retireAge, lifeExp, curSavings, annualContrib, monthlyContrib, returnRate, inflation]);
  const clear = () => { setAge(D.age); setRetireAge(D.retireAge); setLifeExp(D.lifeExp); setCurSavings(D.curSavings); setAnnualContrib(D.annualContrib); setMonthlyContrib(D.monthlyContrib); setReturnRate(D.returnRate); setInflation(D.inflation); setShow(false); };
  return (
    <>
      <h3 className="text-xl font-bold text-slate-900 mb-1">How much can you withdraw after retirement?</h3>
      <p className="text-sm text-slate-600 mb-4">This calculation estimates the amount a person can withdraw every month in retirement.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your age now</label><input type="number" value={age} onChange={e => setAge(+e.target.value || 0)}/></div>
          <div className="field"><label>Your planned retirement age</label><input type="number" value={retireAge} onChange={e => setRetireAge(+e.target.value || 0)}/></div>
          <div className="field"><label>Your life expectancy</label><input type="number" value={lifeExp} onChange={e => setLifeExp(+e.target.value || 0)}/></div>
          <div className="field"><label>Your retirement savings today</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={curSavings} onChange={e => setCurSavings(+e.target.value || 0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual contribution</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={annualContrib} onChange={e => setAnnualContrib(+e.target.value || 0)} className="flex-1"/></div></div>
          <div className="field"><label>Monthly contribution</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={monthlyContrib} onChange={e => setMonthlyContrib(+e.target.value || 0)} className="flex-1" data-testid="ret-wd-mo"/></div></div>
          <div className="field"><label>Average investment return</label><div className="flex gap-2"><input type="number" step="0.1" value={returnRate} onChange={e => setReturnRate(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Inflation rate (annual)</label><div className="flex gap-2"><input type="number" step="0.1" value={inflation} onChange={e => setInflation(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <CNetActions onCalc={() => setShow(true)} onClear={clear} testId="ret-withdraw" />
        </div>
        <div>
          {show ? (<>
            <div className="result-panel mb-4"><div className="result-title">Monthly withdrawal (today's $)</div><div className="result-value" data-testid="ret-wd-value">{fmt(c.monthly)}</div></div>
            <table className="data-table"><tbody>
              <tr><td>Nest egg at retirement (age {retireAge})</td><td className="text-right font-semibold">{fmt(c.nestAtRet)}</td></tr>
              <tr><td>Nominal monthly withdrawal (not adjusted)</td><td className="text-right">{fmt(c.monthlyNominal)}</td></tr>
              <tr><td>Years accumulating</td><td className="text-right">{c.yearsToRet}</td></tr>
              <tr><td>Years withdrawing</td><td className="text-right">{c.yearsInRet}</td></tr>
            </tbody></table>
            <p className="text-xs text-slate-500 mt-2">The monthly withdrawal amount preserves today's purchasing power — future dollar withdrawals will grow with inflation.</p>
          </>) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see your monthly withdrawal.</div>)}
        </div>
      </div>
    </>
  );
}

/* -------- 4. How long can my money last? -------- */
function LifeExpectancy() {
  const D = { nest: 600000, withdraw: 5000, returnRate: 6 };
  const [nest, setNest] = useState(D.nest);
  const [withdraw, setWithdraw] = useState(D.withdraw);
  const [returnRate, setReturnRate] = useState(D.returnRate);
  const [show, setShow] = useState(true);
  const c = useMemo(() => {
    // Simulate month-by-month until balance runs out (max 100 years)
    const rM = Math.pow(1 + returnRate / 100, 1 / 12) - 1;
    let bal = nest, months = 0;
    const rows = [{ year: 0, balance: bal }];   // include starting point so the graph always has data
    while (bal > 0 && months < 1200) {
      bal = bal * (1 + rM) - withdraw;
      months++;
      if (months % 12 === 0 || bal <= 0) rows.push({ year: months / 12, balance: Math.max(0, bal) });
    }
    // If money never runs out (withdrawal < interest), report as sustainable
    const isSustainable = months >= 1200 && bal > 0;
    return { months, years: Math.floor(months / 12), extraMonths: months % 12, rows, isSustainable, finalBal: bal };
  }, [nest, withdraw, returnRate]);
  const clear = () => { setNest(D.nest); setWithdraw(D.withdraw); setReturnRate(D.returnRate); setShow(false); };
  return (
    <>
      <h3 className="text-xl font-bold text-slate-900 mb-1">How long can your money last?</h3>
      <p className="text-sm text-slate-600 mb-4">This calculator estimates how long your savings can last at a given withdrawal rate.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>The amount you have</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={nest} onChange={e => setNest(+e.target.value || 0)} className="flex-1" data-testid="ret-last-nest"/></div></div>
          <div className="field"><label>You plan to withdraw</label><div className="flex gap-2"><span className="self-center">$</span><input type="number" value={withdraw} onChange={e => setWithdraw(+e.target.value || 0)} className="flex-1" data-testid="ret-last-wd"/><span className="self-center text-slate-500">/month</span></div></div>
          <div className="field"><label>Average investment return</label><div className="flex gap-2"><input type="number" step="0.1" value={returnRate} onChange={e => setReturnRate(+e.target.value || 0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <CNetActions onCalc={() => setShow(true)} onClear={clear} testId="ret-life" />
        </div>
        <div>
          {show ? (<>
            <div className="result-panel mb-4"><div className="result-title">Money lasts</div>
              <div className="result-value" data-testid="ret-life-value">{c.isSustainable ? 'Indefinitely — return exceeds withdrawal' : `${c.years} years ${c.extraMonths} months`}</div>
            </div>
            <table className="data-table"><tbody>
              <tr><td>Total months</td><td className="text-right font-semibold">{c.isSustainable ? '∞' : c.months}</td></tr>
              <tr><td>Withdrawal as % of nest egg</td><td className="text-right">{((withdraw * 12) / nest * 100).toFixed(2)}%</td></tr>
              <tr><td>Monthly interest earned initially</td><td className="text-right">{fmt(nest * returnRate/100/12)}</td></tr>
            </tbody></table>
          </>) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see how long your money lasts.</div>)}
        </div>
      </div>
      {show && c.rows.length > 1 && <div className="border border-slate-200 rounded-lg p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Depletion Chart</h3>
        <BalanceGraph yearly={c.rows.map(r => ({ year: r.year, balance: r.balance, interest: 0, principal: 0, payment: 0 }))} startBalance={nest} showPayment={false} />
      </div>}
    </>
  );
}
